import * as React from "react";
import { CheckCircle2, AlertTriangle, Info, X } from "lucide-react";
import { cn } from "./cn.js";

export interface ToastItem {
  id: string;
  tone: "success" | "error" | "info";
  title: string;
  description?: string;
}

interface ToastContextValue {
  toasts: ToastItem[];
  push: (toast: Omit<ToastItem, "id">) => void;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = React.useCallback(
    (toast: Omit<ToastItem, "id">) => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { ...toast, id }]);
      setTimeout(() => dismiss(id), 6000);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toasts, push, dismiss }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cn(
              "pointer-events-auto flex items-start gap-2 rounded-lg border p-3 shadow-lg",
              t.tone === "success" && "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/90",
              t.tone === "error" && "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/90",
              t.tone === "info" && "border-sky-200 bg-sky-50 dark:border-sky-800 dark:bg-sky-900/90",
            )}
          >
            {t.tone === "success" && <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600 dark:text-emerald-300" />}
            {t.tone === "error" && <AlertTriangle className="mt-0.5 h-4 w-4 text-red-600 dark:text-red-300" />}
            {t.tone === "info" && <Info className="mt-0.5 h-4 w-4 text-sky-600 dark:text-sky-300" />}
            <div className="flex-1 text-sm">
              <p className="font-medium text-ink dark:text-slate-50">{t.title}</p>
              {t.description && <p className="text-slate-600 dark:text-slate-300">{t.description}</p>}
            </div>
            <button onClick={() => dismiss(t.id)} aria-label="Schliessen" className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast muss innerhalb von ToastProvider verwendet werden.");
  return ctx;
}
